export const personalInfo = {
  name: "Harish Yerraguntla",
  title: "Full-Stack Software Engineer",
  subtitle: "React, Vue, TypeScript & Node.js | Building Scalable Applications",
  location: "New York, USA",
  phone: "+1 (205) 736-5808",
  email: "yerraguntlaharish98@gmail.com",
  github: "https://github.com/nandu064",
  linkedin: "https://www.linkedin.com/in/harish-yerraguntla-70695416b/",
  portfolio: "https://nandu064.github.io/portfolio/",
};

export const summary = {
  opening:
    "Full-Stack Software Engineer with 5+ years building scalable, high-performance web applications using React.js, Vue.js, TypeScript, and modern JavaScript frameworks. Expert in frontend development with reusable component architectures, custom hooks, and Storybook documentation. Strong backend capabilities with Node.js, Python (FastAPI/Django), and GraphQL. Specialized in real-time data visualization, performance optimization achieving 90+ Lighthouse scores, and building applications serving 2M+ users.",
  highlights: [
    "Built full-stack applications serving 100K+ users with React, Vue, Node.js, and Python achieving 99.8% uptime and 90+ Lighthouse scores",
    "Created reusable component libraries with Storybook documentation using custom hooks and composables, reducing development time by 40%",
    "Architected real-time data visualization dashboards with Plotly.js and D3.js processing 1M+ data points with optimized Canvas rendering",
    "Optimized frontend performance reducing render times from 2-3s to under 500ms and page load times from 8s to 2.5s",
    "Delivered 14+ production applications with modern stack: React, Vue, Next.js, Nuxt.js, TypeScript, Redux Toolkit, Pinia, TanStack Query",
    "Implemented comprehensive testing with Jest, Playwright, Cypress achieving 90%+ coverage in CI/CD pipelines with Docker and Kubernetes",
    "Full-stack expertise: Node.js/Express, Python (FastAPI/Django), GraphQL, PostgreSQL, MongoDB, Redis, AWS, real-time systems with WebSockets and Kafka",
  ],
  coreStack:
    "React.js, Vue.js, Next.js, Nuxt.js, TypeScript, Redux Toolkit, Pinia, TanStack Query, Storybook, Node.js, Python (FastAPI/Django), GraphQL, PostgreSQL, MongoDB, Redis, WebSockets, Kafka, Docker, Kubernetes, AWS, Jest, Playwright, Cypress, Figma, Jira",
};

export const technicalSkills = {
  languages: [
    "JavaScript (ES6+)",
    "TypeScript",
    "Python",
    "SQL",
    "HTML5",
    "CSS3",
  ],
  frontend: [
    "React.js",
    "Vue.js 3",
    "Next.js (SSR/SSG/ISR)",
    "Nuxt.js",
    "React Hooks (custom hooks)",
    "Vue Composition API (custom composables)",
    "Redux Toolkit",
    "Pinia",
    "Vuex",
    "React Context API",
    "TanStack Query (React Query)",
    "Storybook",
    "Tailwind CSS",
    "Material-UI",
    "Vuetify",
    "Ant Design",
    "Styled Components",
    "Sass/SCSS",
    "Responsive Design (CSS Grid, Flexbox)",
  ],
  backend: [
    "Node.js",
    "Express.js",
    "Python (FastAPI, Django)",
    "GraphQL (Apollo Server/Client)",
    "RESTful APIs",
    "WebSockets",
    "Socket.io",
    "Kafka",
    "JWT/OAuth 2.0",
    "Microservices",
  ],
  databases: [
    "PostgreSQL",
    "MongoDB",
    "Redis",
    "MySQL",
    "DynamoDB",
    "Firebase",
    "Database Optimization",
    "Indexing",
    "Query Optimization",
  ],
  cloudDevOps: [
    "Docker",
    "Kubernetes",
    "AWS (Lambda, S3, CloudFront)",
    "GCP",
    "CI/CD (GitHub Actions)",
    "Serverless",
    "Webpack",
    "Vite",
  ],
  testing: [
    "Jest",
    "React Testing Library",
    "Vue Test Utils",
    "Playwright",
    "Cypress",
    "Vitest",
    "TDD",
    "E2E Testing",
  ],
  tools: [
    "Git/GitHub",
    "Figma",
    "Jira",
    "Agile/Scrum",
    "ESLint",
    "Prettier",
    "Chrome DevTools",
    "Lighthouse",
  ],
  dataVisualization: [
    "Plotly.js",
    "D3.js",
    "Chart.js",
    "Recharts",
    "Canvas API",
    "SVG",
    "Real-time Dashboards",
  ],
};

export const experience = [
  {
    id: 1,
    company: "Progress Solutions Inc",
    location: "Texas (Remote)",
    position: "Software Developer",
    startDate: "2025-01",
    endDate: "Present",
    current: true,
    promotionNote: "Promoted from Intern to Full-Time Developer in July 2025",
    description: [
      "Architect and lead full-stack development of React.js, Vue.js, and Next.js applications with TypeScript, designing scalable component libraries with custom hooks, compound component patterns, and Storybook documentation that reduced development time by 40% across 5+ enterprise products serving 50K+ daily users",
      "Build interactive data visualization dashboards using React.js and Vue.js with D3.js and Plotly.js, implementing real-time updates from WebSocket and Kafka streaming processing 1M+ data points with optimized Canvas rendering achieving <500ms render times",
      "Design and implement Python FastAPI and Node.js/Express.js microservices with GraphQL APIs (Apollo Server), implementing JWT/OAuth 2.0 authentication, Redis caching, and PostgreSQL database optimization achieving sub-30ms API response times",
      "Implement advanced performance optimization achieving 90+ Lighthouse scores, reducing initial page load from 6s to <3s through strategic code splitting with React.lazy(), lazy loading, and memoization techniques (React.memo, useMemo, useCallback) that eliminated 60% of unnecessary re-renders",
      "Design complex state management architecture using Redux Toolkit and Pinia with normalized state patterns, custom hooks, and TanStack Query (React Query) for server state management, managing global application state across 50+ components with efficient data fetching and caching reducing API calls by 45%",
      "Establish comprehensive CI/CD pipelines using GitHub Actions with Docker containerization, automated testing with Jest, React Testing Library, and Playwright achieving 90%+ code coverage, and Kubernetes deployment reducing deployment cycles by 70%",
      "Collaborate in Agile/Scrum environment using Jira for sprint planning and task management, working closely with designers in Figma for design handoffs, and conducting code reviews and pair programming sessions with backend engineers",
    ],
    technologies: [
      "React.js",
      "Vue.js",
      "TypeScript",
      "Redux Toolkit",
      "Pinia",
      "TanStack Query",
      "Storybook",
      "Plotly.js",
      "D3.js",
      "Canvas API",
      "Node.js",
      "Python FastAPI",
      "GraphQL",
      "PostgreSQL",
      "MongoDB",
      "Redis",
      "Kafka",
      "Socket.io",
      "Docker",
      "Kubernetes",
      "Jest",
      "Playwright",
      "Cypress",
      "Figma",
      "Jira",
    ],
    keyMetrics: {
      dailyUsers: "50K+",
      dataPoints: "1M+",
      renderTimeImprovement: "83%",
      devTimeReduction: "40%",
      testCoverage: "90%+",
      apiCallReduction: "45%",
      deploymentCycleReduction: "70%",
    },
  },
  {
    id: 2,
    company: "State University of New York",
    location: "New Paltz, NY",
    position: "Frontend Engineer",
    startDate: "2024-01",
    endDate: "2024-12",
    current: false,
    note: "Part-time during Master's program",
    description: [
      "Architected reusable React and Vue component library with TypeScript and Storybook documentation, implementing 40+ components with custom hooks, composables, and compound component patterns deployed across 4 university platforms, reducing UI development time by 30%",
      "Built scalable full-stack applications with React.js and Vue.js frontends, Redux Toolkit and Pinia state management, TanStack Query for server state, and Apollo Client for GraphQL data fetching, implementing custom hooks that reduced unnecessary API calls by 40%",
      "Led backend migration from Node.js to Python FastAPI with MongoDB, implementing JWT authentication middleware, rate limiting, and Redis caching that reduced API response times by 50%",
      "Optimized application performance achieving 45% reduction in initial page load times through advanced code splitting with React.lazy(), memoization techniques (React.memo, useMemo, useCallback), and bundle size optimization reducing bundles by 25%",
      "Deployed full-stack services on GCP with Docker containers and Kubernetes for horizontal scaling, established CI/CD pipelines with GitHub Actions running automated Jest, React Testing Library, and Playwright tests achieving 90%+ code coverage",
    ],
    technologies: [
      "React.js",
      "Vue.js",
      "TypeScript",
      "Redux Toolkit",
      "Pinia",
      "TanStack Query",
      "Storybook",
      "Apollo Client",
      "GraphQL",
      "Python FastAPI",
      "MongoDB",
      "PostgreSQL",
      "Redis",
      "GCP",
      "Docker",
      "Kubernetes",
      "GitHub Actions",
      "Jest",
      "Playwright",
    ],
    keyMetrics: {
      platformsUsed: "4",
      componentsBuilt: "40+",
      devTimeReduction: "30%",
      pageLoadImprovement: "45%",
      apiResponseImprovement: "50%",
      testCoverage: "90%+",
    },
  },
  {
    id: 3,
    company: "Harns Technologies Private LTD",
    location: "India",
    position: "Software Fullstack Developer",
    startDate: "2021-07",
    endDate: "2023-08",
    current: false,
    description: [
      "Led full-stack architecture for 14 production MERN applications (MongoDB, Express.js, React.js, Node.js) and Vue.js applications, directing 4 major enterprise initiatives serving 100K+ daily active users with 99.8% uptime, implementing reusable component libraries with Storybook and custom hooks that reduced cross-team development time by 35%",
      "Built real-time IoT data visualization platform with React.js and D3.js frontend, Node.js backend with WebSocket connections processing live sensor data from 500+ IoT devices with sub-second latency, and MongoDB for time-series data storage",
      "Architected e-paper digital publishing platforms with React.js and Vue.js frontends and Node.js/Express.js RESTful APIs serving 50K+ daily readers, implementing Redux Toolkit and Pinia state management with custom hooks for complex multi-step workflows",
      "Developed enterprise CMS subscription platform with React.js frontend and Python Django/FastAPI backend, implementing Stripe/Razorpay payment integrations processing 10K+ monthly transactions with JWT authentication and PostgreSQL database",
      "Implemented comprehensive performance optimization and SEO achieving 90+ Lighthouse scores and 150% improvement in organic search rankings through code splitting with React.lazy(), lazy loading, memoization, and structured data markup",
      "Established CI/CD pipelines with GitHub Actions and Docker containerization, reducing deployment time by 65%, implementing automated testing with Jest, React Testing Library, and Cypress achieving 85%+ code coverage, working in Agile/Scrum environment with Jira",
    ],
    technologies: [
      "React.js",
      "Vue.js",
      "Redux Toolkit",
      "Pinia",
      "Storybook",
      "D3.js",
      "Node.js",
      "Express.js",
      "Python Django",
      "FastAPI",
      "MongoDB",
      "PostgreSQL",
      "WebSockets",
      "Socket.io",
      "Redis",
      "Docker",
      "Jira",
    ],
    keyMetrics: {
      applicationsDelivered: "14",
      dailyUsers: "100K+",
      uptime: "99.8%",
      iotDevices: "500+",
      monthlyTransactions: "10K+",
      lighthouseScore: "90+",
      devTimeReduction: "35%",
      deploymentTimeReduction: "65%",
    },
  },
  {
    id: 4,
    company: "QS Quacquarelli Symonds Private Limited",
    location: "India",
    position: "Software Developer - Frontend",
    startDate: "2020-12",
    endDate: "2021-07",
    current: false,
    description: [
      "Architected 8+ production React SPAs with TypeScript serving 15M+ annual visitors, building 50+ reusable functional components with custom hooks following component-based architecture and achieving WCAG 2.1 AA accessibility compliance for complex user workflows",
      "Achieved exceptional Lighthouse performance scores 90+ (Performance: 92, Accessibility: 95, Best Practices: 93, SEO: 94) through advanced optimization including code splitting with React.lazy() and React.Suspense, reducing bundle sizes by 55% and initial load times from 8s to 2.5s",
      "Built enterprise-scale state management architecture using Redux Toolkit with normalized state patterns and React Context API across 8 applications, optimizing rendering performance with memoization techniques (React.memo, useMemo, useCallback) reducing unnecessary re-renders by 70%",
      "Established comprehensive CI/CD pipelines using GitHub Actions with automated Jest and React Testing Library testing achieving 85%+ code coverage, containerizing applications with Docker, collaborating with design team using Figma for design handoffs",
    ],
    technologies: [
      "React.js",
      "TypeScript",
      "Redux Toolkit",
      "React Context API",
      "Node.js",
      "Express.js",
      "Redis",
      "Docker",
      "GitHub Actions",
      "Jest",
      "Figma",
    ],
    keyMetrics: {
      spasBuilt: "8",
      annualVisitors: "15M+",
      componentsBuilt: "50+",
      pageLoadImprovement: "69%",
      lighthouseScore: "90+",
      bundleSizeReduction: "55%",
      testCoverage: "85%+",
    },
  },
  {
    id: 5,
    company: "Haindavi Designer Studio",
    location: "India",
    position: "Fullstack Developer",
    startDate: "2019-12",
    endDate: "2020-11",
    current: false,
    description: [
      "Architected complete full-stack e-commerce platform using MERN stack (MongoDB, Express.js, React.js, Node.js) with 40+ reusable components and custom hooks serving 5K+ monthly users, implementing Redux Toolkit state management and RESTful APIs for cart operations and payment processing",
      "Achieved Lighthouse scores 92+ (Performance: 92, Accessibility: 94, SEO: 96) reducing page load times by 65% from 9s to 3.1s through strategic code splitting with React.lazy() and Core Web Vitals optimization (LCP: 2.1s, CLS: 0.08, INP: 180ms)",
      "Built interactive product customization interface with React and Canvas API featuring real-time preview with drag-and-drop functionality, increasing customer engagement by 60% and custom order conversions by 45%",
      "Integrated secure payment gateways (Stripe, PayPal, Razorpay) through Node.js backend with PCI compliance processing 2,500+ transactions, implemented comprehensive SEO optimization with structured data markup, set up CI/CD with GitHub Actions for automated testing and deployment",
    ],
    technologies: [
      "React.js",
      "Redux Toolkit",
      "Node.js",
      "Express.js",
      "MongoDB",
      "Canvas API",
      "Stripe",
      "PayPal",
      "Razorpay",
      "GitHub Actions",
    ],
    keyMetrics: {
      componentsBuilt: "40+",
      monthlyUsers: "5K+",
      engagementIncrease: "60%",
      conversionIncrease: "45%",
      lighthouseScore: "92+",
      pageLoadReduction: "65%",
      transactionsProcessed: "2500+",
    },
  },
];

export const education = [
  {
    id: 1,
    degree: "Master of Science in Computer Science",
    institution: "State University of New York",
    location: "New Paltz, NY",
    startDate: "2023",
    endDate: "2025",
    current: false,
    gpa: "3.81/4.0",
    coursework:
      "Web Development, Full-Stack Development, Database Systems, Software Engineering, Cloud Computing",
  },
  {
    id: 2,
    degree: "Bachelor of Technology in Computer Science",
    institution: "Lovely Professional University",
    location: "Punjab, India",
    startDate: "2017",
    endDate: "2021",
    current: false,
    gpa: "3.2/4.0",
  },
];

export const projects = [
  {
    id: 0,
    title: "Personal Portfolio Website",
    description:
      "Modern React.js portfolio with interactive chatbot powered by intelligent Q&A matching, responsive design, and optimized performance. Features custom animations, component library, and automated deployment.",
    image: "/portfolio_thumbnail.png",
    technologies: ["React.js", "SCSS", "EmailJS", "Bootstrap", "GitHub Pages"],
    category: "Frontend",
    metrics: "Lighthouse 90+, Mobile Optimized",
    github: "https://github.com/Nandu064/portfolio",
    demo: "https://nandu064.github.io/portfolio/",
  },
  {
    id: 1,
    title: "Real-Time Data Visualization Dashboard",
    description:
      "React/Vue dashboards with Plotly.js and D3.js processing 1M+ data points with Canvas API optimization, WebSocket streaming from Kafka, reducing render times from 2-3s to <500ms.",
    image: "/api/placeholder/400/250",
    technologies: [
      "React.js",
      "Vue.js",
      "Plotly.js",
      "D3.js",
      "Canvas API",
      "Kafka",
      "WebSocket",
    ],
    category: "Full-Stack",
    metrics: "1M+ data points, 83% faster rendering",
    github: "https://github.com/nandu064",
    demo: "https://demo-link.com",
  },
  {
    id: 2,
    title: "Component Library with Storybook",
    description:
      "Reusable React and Vue component library with TypeScript, custom hooks/composables, and Storybook documentation, reducing development time by 40% across multiple platforms.",
    image: "/api/placeholder/400/250",
    technologies: [
      "React.js",
      "Vue.js",
      "TypeScript",
      "Storybook",
      "Redux Toolkit",
      "Pinia",
    ],
    category: "Frontend",
    metrics: "40+ components, 40% faster development",
    github: "https://github.com/nandu064",
    demo: "https://demo-link.com",
  },
  {
    id: 3,
    title: "Real-Time IoT Data Platform",
    description:
      "Full-stack platform with React/Vue frontend, Node.js backend processing WebSocket data from 500+ IoT devices with MongoDB time-series storage and sub-second latency.",
    image: "/api/placeholder/400/250",
    technologies: [
      "React.js",
      "Vue.js",
      "D3.js",
      "Node.js",
      "MongoDB",
      "WebSocket",
      "Redis",
    ],
    category: "Full-Stack",
    metrics: "500+ devices, <1s latency",
    github: "https://github.com/nandu064",
    demo: "https://demo-link.com",
  },
  {
    id: 4,
    title: "E-Commerce Platform with Payment Integration",
    description:
      "Complete MERN stack e-commerce platform with React frontend, Redux Toolkit state management, Node.js backend, Stripe/Razorpay integration processing 10K+ monthly transactions.",
    image: "/api/placeholder/400/250",
    technologies: [
      "React.js",
      "Redux Toolkit",
      "Node.js",
      "MongoDB",
      "Stripe",
      "Razorpay",
    ],
    category: "Full-Stack",
    metrics: "10K+ monthly transactions",
    github: "https://github.com/nandu064",
    demo: "https://demo-link.com",
  },
  {
    id: 5,
    title: "GraphQL API with Apollo Server",
    description:
      "Scalable GraphQL API with Node.js, Apollo Server, MongoDB, and Redis caching achieving sub-30ms response times for complex queries with JWT authentication.",
    image: "/api/placeholder/400/250",
    technologies: [
      "GraphQL",
      "Apollo Server",
      "Node.js",
      "MongoDB",
      "Redis",
      "JWT",
    ],
    category: "Backend",
    metrics: "<30ms response times",
    github: "https://github.com/nandu064",
    demo: "https://demo-link.com",
  },
  {
    id: 6,
    title: "Digital Publishing Platform",
    description:
      "React/Vue e-paper platforms serving 50K+ daily readers with Node.js backend, real-time content updates, and 99.8% uptime.",
    image: "/api/placeholder/400/250",
    technologies: ["React.js", "Vue.js", "Node.js", "Express.js", "MongoDB"],
    category: "Full-Stack",
    metrics: "50K+ daily readers, 99.8% uptime",
    github: "https://github.com/nandu064",
    demo: "https://demo-link.com",
  },
  {
    id: 7,
    title: "CI/CD Pipeline with Docker & Kubernetes",
    description:
      "Automated deployment pipeline with GitHub Actions, Docker containers, Kubernetes orchestration, Jest/Playwright/Cypress testing achieving 90%+ coverage.",
    image: "/api/placeholder/400/250",
    technologies: [
      "Docker",
      "Kubernetes",
      "GitHub Actions",
      "Jest",
      "Playwright",
      "Cypress",
      "AWS",
    ],
    category: "DevOps",
    metrics: "90%+ test coverage, 70% faster deployments",
    github: "https://github.com/nandu064",
    demo: "https://demo-link.com",
  },
];

export const metrics = {
  totalExperience: "5+ years",
  projectsCompleted: "14+",
  usersServed: "2M+",
  uptime: "99.8%",
  lighthouseScore: "90+",
  testCoverage: "90%+",
  performanceImprovement: "83%",
  renderTimeOptimization: "2-3s to <500ms",
};

export const highlights = [
  {
    category: "Full-Stack Development",
    items: [
      "React, Vue, Next.js, Nuxt.js with TypeScript",
      "Node.js, Python (FastAPI/Django), GraphQL",
      "PostgreSQL, MongoDB, Redis caching",
      "Docker, Kubernetes, AWS deployment",
    ],
  },
  {
    category: "Performance",
    items: [
      "Achieved 90+ Lighthouse scores",
      "Reduced render times by 83%",
      "Optimized to <3s load times",
      "99.8% uptime across platforms",
    ],
  },
  {
    category: "Scale",
    items: [
      "Serving 2M+ users",
      "Processing 1M+ data points",
      "Handling 15M+ annual visitors",
      "Managing 500+ IoT devices",
    ],
  },
  {
    category: "Modern Tooling",
    items: [
      "Storybook component documentation",
      "TanStack Query for server state",
      "Playwright/Cypress E2E testing",
      "Figma design collaboration, Jira Agile",
    ],
  },
];

export const metadata = {
  totalYearsExperience: "5+",
  totalProjectsLed: "14+",
  companiesWorkedFor: 5,
  currentlyEmployed: true,
  openToWork: true,
  workAuthorization: "F-1 OPT with STEM extension valid through June 2028",
  preferredRoles: [
    "Full-Stack Software Engineer",
    "Frontend Engineer",
    "Software Engineer",
    "React Developer",
    "Vue.js Developer",
  ],
  topSkills: [
    "React.js",
    "Vue.js",
    "TypeScript",
    "Node.js",
    "Python",
    "Redux Toolkit",
    "Pinia",
    "TanStack Query",
    "Storybook",
    "GraphQL",
  ],
  resumeVersion: "FullStack-Frontend-Heavy-v2.0",
  lastUpdated: "2025-12-12",
};
